import { IStorage } from './IStorage';

// Prefixes for keys that are safe to evict under storage pressure: they're
// either regenerated automatically (i18next fetches translations on demand)
// or are third-party SDK internals we don't own (CleverTap's WZRK_* keys).
const EVICTABLE_KEY_PREFIXES = [/^WZRK_/, /^i18next_res_/];

// CleverTap's web SDK backs up every outgoing analytics event to WZRK_L before
// sending it, and only clears the backup once it gets a confirmed response
// (see its RequestDispatcher). In webviews where those requests never
// round-trip (e.g. the Word add-in's embedded browser), WZRK_L grows without
// bound instead of staying at a few KB, eventually consuming the whole quota.
// Legitimate in-flight backups never realistically reach this size.
const BLOAT_THRESHOLD_BYTES = 200 * 1024;

export class LocalStorageWrapper implements IStorage {
  constructor() {
    this.pruneKnownBloat();
  }

  /**
   * Proactively clear known third-party SDK keys (currently CleverTap's
   * WZRK_*) if they've grown past a size that only happens when they're
   * stuck, not when they hold legitimate transient data. Runs once at
   * storage init so a runaway key can't consume the quota before anything
   * else gets a chance to write.
   */
  private pruneKnownBloat(): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      const keysToCheck: string[] = [];
      for (const key in localStorage) {
        // eslint-disable-next-line
        if (localStorage.hasOwnProperty(key) && /^WZRK_/.test(key)) {
          keysToCheck.push(key);
        }
      }

      for (const key of keysToCheck) {
        const value = localStorage.getItem(key);
        const size = key.length + (value ? value.length : 0);
        if (size > BLOAT_THRESHOLD_BYTES) {
          console.warn(
            `[Storage] Clearing oversized tracking key "${key}" (~${(
              size / 1024
            ).toFixed(2)} KB) at startup`,
          );
          localStorage.removeItem(key);
        }
      }
    } catch (e) {
      // Best-effort only; never block app init on this.
    }
  }

  /**
   * Calculate the total size of all items in storage.
   * Returns size in bytes.
   */
  private calculateStorageSize(storage: Storage): number {
    let totalSize = 0;
    for (const key in storage) {
      // eslint-disable-next-line
      if (storage.hasOwnProperty(key)) {
        try {
          const value = storage.getItem(key);
          // Count key + value length (both as strings)
          totalSize += key.length + (value ? value.length : 0);
        } catch (e) {
          // Skip if access fails
        }
      }
    }
    return totalSize;
  }

  /**
   * Get detailed information about all storage items and their sizes.
   * Returns an array of items sorted by size (largest first).
   */
  private getStorageDetails(
    storage: Storage,
  ): Array<{ key: string; size: number; sizeKB: string; preview: string }> {
    const items: Array<{
      key: string;
      size: number;
      sizeKB: string;
      preview: string;
    }> = [];

    for (const key in storage) {
      // eslint-disable-next-line
      if (storage.hasOwnProperty(key)) {
        try {
          const value = storage.getItem(key);
          const size = (value ? value.length : 0) + key.length;
          const sizeKB = (size / 1024).toFixed(2);
          // Get a preview of the value (first 100 chars)
          const preview =
            value && value.length > 100
              ? `${value.substring(0, 100)}...`
              : value || '(empty)';

          items.push({
            key,
            size,
            sizeKB,
            preview,
          });
        } catch (e) {
          // Skip if access fails
        }
      }
    }

    // Sort by size descending
    return items.sort((a, b) => b.size - a.size);
  }

  /**
   * Log detailed storage information for debugging.
   */
  private logStorageInfo(storage: Storage, storageType: string): void {
    try {
      const totalSize = this.calculateStorageSize(storage);
      const totalSizeKB = (totalSize / 1024).toFixed(2);
      const totalSizeMB = (totalSize / 1024 / 1024).toFixed(2);
      const items = this.getStorageDetails(storage);

      console.warn(
        `[Storage Debug - ${storageType}] Total size: ${totalSizeKB} KB (~${totalSizeMB} MB)`,
      );
      console.warn(
        `[Storage Debug - ${storageType}] Total items: ${items.length}`,
      );
      console.warn(
        `[Storage Debug - ${storageType}] Items breakdown (sorted by size): ${JSON.stringify(
          items.map((item) => ({
            key: item.key,
            sizeKB: item.sizeKB,
            preview: item.preview,
          })),
        )}`,
      );
    } catch (e) {
      console.error(`[Storage Debug] Error logging storage info:`, e);
    }
  }

  /**
   * Clear non-critical storage items to free up space: known evictable
   * prefixes (WZRK_*, i18next_res_*), which are regenerated automatically and
   * never touch auth state. Removes largest items first (getStorageDetails is
   * sorted descending) and returns bytes freed.
   */
  private clearStorageSpace(storage: Storage, storageType: string): number {
    console.warn(`[Storage Cleanup] Initiating cleanup for ${storageType}...`);

    const candidates = this.getStorageDetails(storage).filter((item) =>
      EVICTABLE_KEY_PREFIXES.some((pattern) => pattern.test(item.key)),
    );

    let freedBytes = 0;
    for (const item of candidates) {
      try {
        storage.removeItem(item.key);
        freedBytes += item.size;
        console.warn(
          `[Storage Cleanup] Removed "${item.key}" (~${item.sizeKB} KB) to free space`,
        );
      } catch (e) {
        // If removal fails, continue to next item
      }
    }

    return freedBytes;
  }

  async getItem(
    key: string,
    type?: 'session',
  ): Promise<string | null | undefined> {
    return new Promise<string | null | undefined>((resolve, reject) => {
      let value = undefined;
      try {
        if (typeof window !== 'undefined') {
          if (type === 'session') {
            value = sessionStorage.getItem(key);
          } else {
            value = localStorage.getItem(key);
          }
          resolve(value);
        }
        resolve(null);
      } catch (e) {
        console.error('Error accessing storage:', e);
        reject(e);
      }
    });
  }

  async setItem(key: string, value: string, type?: 'session'): Promise<void> {
    try {
      if (typeof window !== 'undefined') {
        const storage = type === 'session' ? sessionStorage : localStorage;

        try {
          storage.setItem(key, value);
        } catch (e) {
          const storageType =
            type === 'session' ? 'sessionStorage' : 'localStorage';
          // If QuotaExceededError, try to clear space and retry once
          if (e instanceof DOMException && e.name === 'QuotaExceededError') {
            console.error(
              `[Storage] QuotaExceededError when setting "${key}" (${(
                (value.length + key.length) /
                1024
              ).toFixed(2)} KB) in ${storageType}.`,
            );

            // Log current storage state for debugging
            this.logStorageInfo(storage, `QuotaExceeded - ${storageType}`);

            const freedBytes = this.clearStorageSpace(storage, storageType);

            if (freedBytes === 0) {
              throw e;
            }

            // Retry setting the item now that space has been freed
            try {
              storage.setItem(key, value);
              console.warn(
                `[Storage] Successfully stored "${key}" after cleanup (freed ~${(
                  freedBytes / 1024
                ).toFixed(2)} KB)`,
              );
            } catch (retryError) {
              console.error(
                `Error setting item in storage after cleanup attempt for key "${key}":`,
                retryError,
              );
              // Log storage state after failed retry
              this.logStorageInfo(
                storage,
                `After Failed Retry - ${storageType}`,
              );
              throw retryError;
            }
          } else {
            throw e;
          }
        }
      }
    } catch (e) {
      console.error('Error setting item in storage:', e);
      throw e;
    }
  }

  async removeItem(key: string, type?: 'session'): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        if (typeof window !== 'undefined') {
          if (type === 'session') {
            sessionStorage.removeItem(key);
          } else {
            localStorage.removeItem(key);
          }
        }
        resolve();
      } catch (e) {
        console.error('Error removing item from storage:', e);
        reject(e);
      }
    });
  }
}
